import { useMemo } from 'react';

import { makeLinearScale } from 'helper/wordcloud/makeLinearScale';
import { calculateAdjustedFontSize } from 'helper/wordcloud/fontSizeAdjustment';

const WORDCLOUD_COLORS = ['#2C2A43', '#659DFF', '#42578D', '#5985D8', '#6DB1F9'];

/**
 * Hook to generate wordcloud configuration based on words and responsive settings
 *
 * @param {Object} params - Parameters for the wordcloud configuration
 * @param {Array} params.words - Array of word objects with text, value, and versions
 * @param {Object} params.responsiveConfig - Responsive configuration for the wordcloud
 * @param {Object} params.canvasDimensions - Canvas dimensions (width and height)
 * @param {Function} params.onWordHover - Callback for word hover event
 * @returns {Object | null} Wordcloud configuration
 */
export const useWordCloudConfig = ({ words, responsiveConfig, canvasDimensions, onWordHover }) => {
  return useMemo(() => {
    if (!words.length || !canvasDimensions.width || !canvasDimensions.height) {
      return null;
    }

    const { width, height } = canvasDimensions;
    const minSide = Math.min(width, height);

    const { fontSizes, sameWeightValue, gridSizeFactor } = responsiveConfig;
    const [minPx, originalMaxPx] = fontSizes;
    const minWeight = Math.min(...words.map((w) => w.value));
    const maxWeight = Math.max(...words.map((w) => w.value));

    const adjustedMaxPx = calculateAdjustedFontSize({
      words,
      originalMaxPx,
      minPx,
      width,
      height,
    });

    const sizeScale = makeLinearScale(minWeight, maxWeight, minPx, adjustedMaxPx, sameWeightValue);

    return {
      list: words.map(({ text, value, versions }) => [text, value, versions]),
      fontFamily: 'Poppins',
      fontWeight: 500,
      backgroundColor: 'transparent',
      minRotation: 0,
      maxRotation: 0,
      rotateRatio: 0,
      minSize: 0,
      shape: 'ellipse',
      ellipticity: height / width,
      weightFactor: sizeScale,
      gridSize: Math.round((gridSizeFactor * minSide) / 1024),
      color: () => {
        const randomIndex = Math.floor(Math.random() * WORDCLOUD_COLORS.length);

        return WORDCLOUD_COLORS[randomIndex];
      },
      hover: onWordHover,
    };
  }, [words, responsiveConfig, canvasDimensions, onWordHover]);
};
